'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var index = require('./iframeMessaging/index.js');
var messageSchemas = require('./iframeMessaging/messageSchemas.js');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var React__default = /*#__PURE__*/_interopDefaultLegacy(React);

const ClaimsAgentRootContext = /*#__PURE__*/React__default["default"].createContext(null);
const useClaimsAgentRoot = () => {
  const context = React.useContext(ClaimsAgentRootContext);
  if (!context) {
    throw new Error('No context provided for ClaimsAgentRoot provider');
  }
  return context;
};

/**
 * This provider saves refs of the current auth and parent origin url. parentOrigin
 * get set in ClaimsProviders after making a request for quin settings to get the
 * retailer support platform origin, and auth gets set after we get use the parentOrigin
 * to communicate with the support platform via postMessage to get the token. So,
 * these two values first depend on quin settings getting called, which depends on the
 * apollo client being set up. But apollo client is also dependent on these two values
 * because it needs to send the token on agent claims queries, and if the token becomes
 * invalid, it needs to communicate with the support platform via postMessage to get
 * a new token. So kind of a cyclical dependency thing here I'm solving for. And these
 * values are being saved as refs instead of state because they're only being accessed
 * in apollo, and I don't want the rest of the tree to update when these values change.
 */
const ClaimsAgentRootProvider = _ref => {
  let {
    children
  } = _ref;
  const auth = React.useRef(null);
  const setAuth = newToken => auth.current = newToken ? `Bearer ${newToken}` : null;
  const getAuth = () => auth.current;
  const parentOrigins = React.useRef(null);
  const setParentOrigins = origin => parentOrigins.current = origin;

  // We can manually request that a new token is generated
  const refreshAuth = async () => {
    const requestMessage = {
      type: messageSchemas.MESSAGE_TYPES.NEW_TOKEN_REQUEST
    };
    const result = await index.iframeQuery({
      origins: parentOrigins.current,
      requestMessage,
      responseMessageSchema: messageSchemas.newTokenResponseSchema
    });
    setAuth(result.payload.token);
  };
  React.useEffect(() => {
    // We can listen for a new token if the support platform tells the agent portal
    // that the token has expired
    function handleReceiveMessage(event) {
      if (!index.isAllowedMessageOrigin({
        origins: parentOrigins.current,
        event
      })) {
        return;
      }
      const response = messageSchemas.newTokenResponseSchema.safeParse(event.data);
      if (response.success) {
        setAuth(response.data.payload.token);
      }
    }
    window.addEventListener('message', handleReceiveMessage, false);
    return () => {
      window.removeEventListener('message', handleReceiveMessage);
    };
  }, []);
  return /*#__PURE__*/React__default["default"].createElement(ClaimsAgentRootContext.Provider, {
    value: {
      getAuth,
      setAuth,
      refreshAuth,
      setParentOrigins
    }
  }, children);
};

exports.ClaimsAgentRootContext = ClaimsAgentRootContext;
exports.ClaimsAgentRootProvider = ClaimsAgentRootProvider;
exports.useClaimsAgentRoot = useClaimsAgentRoot;
